import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const PasswordRecoveryVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location?.state?.email || '';
  const accountType = location?.state?.accountType || 'user';

  const [step, setStep] = useState('verification'); // 'verification' or 'password'
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const colors = accountType === 'business' 
    ? { primary: 'blue', gradient: 'from-blue-50 via-white to-blue-50' }
    : { primary: 'emerald', gradient: 'from-emerald-50 via-white to-emerald-50' };

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      navigate('/password-recovery-request', { state: { accountType } });
    }
  }, [email, navigate, accountType]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const maskEmail = (email) => {
    if (!email) return '';
    const [username, domain] = email?.split('@');
    const maskedUsername = username?.charAt(0) + '***' + username?.charAt(username?.length - 1);
    return `${maskedUsername}@${domain}`;
  };

  const validateCode = () => {
    const newErrors = {};
    
    if (!verificationCode?.trim()) {
      newErrors.code = 'Por favor ingresa el código de verificación';
    } else if (verificationCode?.length !== 6) {
      newErrors.code = 'El código debe tener 6 dígitos';
    } else if (!/^\d{6}$/?.test(verificationCode)) {
      newErrors.code = 'El código solo debe contener números';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};

    if (!newPassword) {
      newErrors.newPassword = 'La contraseña es requerida';
    } else {
      if (newPassword?.length < 8) {
        newErrors.newPassword = 'La contraseña debe tener al menos 8 caracteres';
      }
      if (!/[A-Z]/?.test(newPassword)) {
        newErrors.newPassword = 'Debe incluir al menos una letra mayúscula';
      }
      if (!/[a-z]/?.test(newPassword)) {
        newErrors.newPassword = 'Debe incluir al menos una letra minúscula';
      }
      if (!/[0-9]/?.test(newPassword)) {
        newErrors.newPassword = 'Debe incluir al menos un número';
      }
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Por favor confirma tu contraseña';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const getPasswordStrength = () => {
    if (!newPassword) return { strength: 0, label: '', color: 'gray' };
    
    let strength = 0;
    if (newPassword?.length >= 8) strength++;
    if (newPassword?.length >= 12) strength++;
    if (/[A-Z]/?.test(newPassword)) strength++;
    if (/[a-z]/?.test(newPassword)) strength++;
    if (/[0-9]/?.test(newPassword)) strength++;
    if (/[^A-Za-z0-9]/?.test(newPassword)) strength++;

    if (strength <= 2) return { strength: 33, label: 'Débil', color: 'red' };
    if (strength <= 4) return { strength: 66, label: 'Media', color: 'yellow' };
    return { strength: 100, label: 'Fuerte', color: 'green' };
  };

  const handleVerifyCode = async (e) => {
    e?.preventDefault();
    
    if (!validateCode()) return;

    setIsLoading(true);

    // Simulate API call to verify code
    setTimeout(() => {
      setIsLoading(false);
      // TODO: Replace with actual verification logic
      // For now, accept any 6-digit code
      setStep('password');
    }, 1500);
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault();
    
    if (!validatePassword()) return;

    setIsLoading(true);

    // Simulate API call to reset password
    setTimeout(() => {
      setIsLoading(false);
      // Navigate to login page
      if (accountType === 'business') {
        navigate('/business-login', { 
          state: { message: 'Contraseña restablecida exitosamente. Por favor inicia sesión.' }
        });
      } else {
        navigate('/user-login', { 
          state: { message: 'Contraseña restablecida exitosamente. Por favor inicia sesión.' }
        });
      }
    }, 1500);
  };

  const handleResendCode = () => {
    if (!canResend) return;
    
    setCanResend(false);
    setResendTimer(60);
    // TODO: Implement actual resend logic
    console.log('Resending code to:', email);
  };

  const handleChangeEmail = () => {
    navigate('/password-recovery-request', { state: { accountType } });
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors?.gradient} flex items-center justify-center p-4`}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className={`w-20 h-20 bg-${colors?.primary}-600 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
            <Icon name={step === 'verification' ? 'ShieldCheck' : 'Lock'} size={40} color="#FFFFFF" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {step === 'verification' ? 'Verificar Código' : 'Nueva Contraseña'}
          </h1>
          <p className="text-gray-600">
            {step === 'verification' 
              ? 'Ingresa el código que enviamos a tu correo' :'Crea una contraseña segura para tu cuenta'
            }
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {step === 'verification' ? (
            <>
              {/* Email Confirmation */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon name="Mail" size={20} className={`text-${colors?.primary}-600`} />
                    <div>
                      <p className="text-xs text-gray-600">Código enviado a:</p>
                      <p className="text-sm font-medium text-gray-900">{maskEmail(email)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleChangeEmail}
                    className={`text-xs text-${colors?.primary}-600 hover:text-${colors?.primary}-700 font-medium`}
                  >
                    Cambiar
                  </button>
                </div>
              </div>

              {/* Verification Form */}
              <form onSubmit={handleVerifyCode} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Código de Verificación
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e?.target?.value?.replace(/\D/g, '')?.slice(0, 6);
                      setVerificationCode(value);
                      setErrors({});
                    }}
                    placeholder="000000"
                    maxLength={6}
                    className={`w-full px-4 py-3 text-center text-2xl font-bold tracking-widest border ${
                      errors?.code ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-${colors?.primary}-500`}
                  />
                  {errors?.code && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <Icon name="AlertCircle" size={16} />
                      {errors?.code}
                    </p>
                  )}
                </div>

                {/* Resend Code */}
                <div className="text-center">
                  {canResend ? (
                    <button
                      type="button"
                      onClick={handleResendCode}
                      className={`text-sm text-${colors?.primary}-600 hover:text-${colors?.primary}-700 font-medium`}
                    >
                      Reenviar código
                    </button>
                  ) : (
                    <p className="text-sm text-gray-600">
                      Reenviar código en <span className="font-medium">{resendTimer}s</span>
                    </p>
                  )}
                </div>

                {/* Verify Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full bg-${colors?.primary}-600 hover:bg-${colors?.primary}-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isLoading ? (
                    <>
                      <Icon name="Loader2" size={20} className="animate-spin" />
                      <span>Verificando...</span>
                    </>
                  ) : (
                    <>
                      <span>Verificar Código</span>
                      <Icon name="ArrowRight" size={20} />
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Password Reset Form */}
              <form onSubmit={handleResetPassword} className="space-y-6">
                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nueva Contraseña
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Lock" size={20} className="text-gray-400" />
                    </div>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e?.target?.value);
                        setErrors({});
                      }}
                      placeholder="Ingresa tu nueva contraseña"
                      className={`w-full pl-10 pr-12 py-3 border ${
                        errors?.newPassword ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-${colors?.primary}-500`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <Icon 
                        name={showNewPassword ? 'EyeOff' : 'Eye'} 
                        size={20} 
                        className="text-gray-400 hover:text-gray-600"
                      />
                    </button>
                  </div>
                  {errors?.newPassword && (
                    <p className="mt-2 text-sm text-red-600">{errors?.newPassword}</p>
                  )}
                  
                  {/* Password Strength Indicator */}
                  {newPassword && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-600">Seguridad:</span>
                        <span className={`text-xs font-medium text-${passwordStrength?.color}-600`}>
                          {passwordStrength?.label}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`bg-${passwordStrength?.color}-500 h-2 rounded-full transition-all duration-300`}
                          style={{ width: `${passwordStrength?.strength}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Contraseña
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Lock" size={20} className="text-gray-400" />
                    </div>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e?.target?.value);
                        setErrors({});
                      }}
                      placeholder="Confirma tu nueva contraseña"
                      className={`w-full pl-10 pr-12 py-3 border ${
                        errors?.confirmPassword ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-${colors?.primary}-500`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <Icon 
                        name={showConfirmPassword ? 'EyeOff' : 'Eye'} 
                        size={20} 
                        className="text-gray-400 hover:text-gray-600"
                      />
                    </button>
                  </div>
                  {errors?.confirmPassword && (
                    <p className="mt-2 text-sm text-red-600">{errors?.confirmPassword}</p>
                  )}
                </div>

                {/* Password Requirements */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-900 mb-2">Requisitos de contraseña:</p>
                  <ul className="space-y-1 text-xs text-gray-600">
                    <li className="flex items-center gap-2">
                      <Icon 
                        name={newPassword?.length >= 8 ? 'CheckCircle2' : 'Circle'} 
                        size={14} 
                        className={newPassword?.length >= 8 ? 'text-green-600' : 'text-gray-400'}
                      />
                      Mínimo 8 caracteres
                    </li>
                    <li className="flex items-center gap-2">
                      <Icon 
                        name={/[A-Z]/?.test(newPassword) ? 'CheckCircle2' : 'Circle'} 
                        size={14} 
                        className={/[A-Z]/?.test(newPassword) ? 'text-green-600' : 'text-gray-400'}
                      />
                      Una letra mayúscula
                    </li>
                    <li className="flex items-center gap-2">
                      <Icon 
                        name={/[a-z]/?.test(newPassword) ? 'CheckCircle2' : 'Circle'} 
                        size={14} 
                        className={/[a-z]/?.test(newPassword) ? 'text-green-600' : 'text-gray-400'}
                      />
                      Una letra minúscula
                    </li>
                    <li className="flex items-center gap-2">
                      <Icon 
                        name={/[0-9]/?.test(newPassword) ? 'CheckCircle2' : 'Circle'} 
                        size={14} 
                        className={/[0-9]/?.test(newPassword) ? 'text-green-600' : 'text-gray-400'}
                      />
                      Un número
                    </li>
                  </ul>
                </div>

                {/* Reset Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full bg-${colors?.primary}-600 hover:bg-${colors?.primary}-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isLoading ? (
                    <>
                      <Icon name="Loader2" size={20} className="animate-spin" />
                      <span>Restableciendo...</span>
                    </>
                  ) : (
                    <>
                      <span>Restablecer Contraseña</span>
                      <Icon name="Check" size={20} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordRecoveryVerification;